import React, { useEffect, useContext, useState, useCallback } from 'react'
import { io } from 'socket.io-client'
import { env, socketUrl } from '../constants/Connections'
import { useUser } from '../auth'
import useWindowFocus from 'use-window-focus'

const SocketContext = React.createContext()

function SocketProvider(props) {
    const [socket, setSocket] = useState(null)
    const [socketStates, setSocketStates] = useState({ messages: [], unseenConversations: [], lastMessage: null, seenList: [] })
    const { user } = useUser()
    const windowFocused = useWindowFocus()
    const changeSocketState = useCallback(update => setSocketStates(old => ({ ...old, ...update })), [])
    const userId = user?.id
    const token = user?.token

    useEffect(() => {
        if (!userId || !token) return
        if (env === 'local') return
        const client = io(socketUrl, {
            autoConnect: true, timeout: 2000,
            // Browser WebSocket handshakes cannot attach Authorization headers.
            // The v4 server accepts auth.token; legacy mobile header/query auth stays supported.
            auth: { token },
        })
        const connected = () => {
            setSocket(client)
            client.emit('user_connected', userId)
        }
        client.on('connect', connected)
        return () => {
            client.off('connect', connected)
            client.disconnect()
            setSocket(null)
        }
    }, [userId, token])

    useEffect(() => {
        if (!socket) return
        const message = data => setSocketStates(old => {
            const fromOtherUser = data.user_id !== userId
            const conversation = data.conversation_id
            const unseen = fromOtherUser && !old.unseenConversations.includes(conversation) &&
                (conversation !== old.openedConversation || !windowFocused)
            return {
                ...old, lastMessage: data,
                messages: fromOtherUser ? [...old.messages, data] : old.messages,
                unseenConversations: unseen ? [...old.unseenConversations, conversation] : old.unseenConversations,
            }
        })
        const seen = data => setSocketStates(old => old.seenList.some(item => item.id === data.id) ? old : {
            ...old, seenList: [...old.seenList, data], lastMessage: data,
        })
        const selfSeen = data => changeSocketState({ lastMessage: data })
        socket.on('new_message', message)
        socket.on('new_seen', seen)
        socket.on('self_seen', selfSeen)
        return () => {
            socket.off('new_message', message)
            socket.off('new_seen', seen)
            socket.off('self_seen', selfSeen)
        }
    }, [socket, userId, windowFocused, changeSocketState])

    return <SocketContext.Provider value={{ socket, setSocket, socketStates, changeSocketState }} {...props} />
}

function useSocket() {
    const context = useContext(SocketContext)
    if (context === undefined) throw new Error('useSocket must be used within a SocketProvider')
    return context
}

export { SocketProvider, useSocket }
